import React, { useState, useEffect } from 'react';
import { 
  Car, 
  VideoCamera, 
  Buildings, 
  SidebarSimple,
  Drop,
  WashingMachine,
  Bathtub,
  Cube,
  Compass,
  TreePalm,
  Question,
  Check,
  X,
  PencilSimple,
  DeviceMobile,
  Oven,
  ThermometerCold,
  WifiHigh,
  Plus,
  Eye,
  ForkKnife,
  CookingPot,
  Lightning,
  Snowflake,
  Info,
  CalendarBlank,
  CurrencyEur,
  Bed,
  Users,
  UserCircle,
  LinkSimple,
  Envelope,
  Globe,
  Trash,
  Shower,
  Translate,
  Handshake,
} from '@phosphor-icons/react';
import { EditableTextField } from './fields/EditableTextField';
import { EditableNumberField } from './fields/EditableNumberField';
import { EditableSelectField } from './fields/EditableSelectField';
import { EditableDateField } from './fields/EditableDateField';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { Tooltip, TooltipTrigger, TooltipContent } from 'components/ui/tooltip';
import { UnitDTO } from 'shared/types/unit';
import { Device, DeviceType } from 'shared/types/device';
import { applyMask } from 'shared/utils/masks';
import DeviceDetailModal from 'screens/buildings/components/operationalDashboard/modals/devices/devicesDetailModal';
import DirectSearchComponent, { SearchMode } from '../searchBar/components/directSearchComponent';
import { ResourceType } from 'shared/types/resource';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { BrokerListCell } from '../rental/components/BrokerListCell';

// Translation Types
export enum SupportedLanguage {
  NL = 'nl', // Dutch (original)
  EN = 'en', // English
  FR = 'fr', // French
  ES = 'es', // Spanish
  PL = 'pl', // Polish
  UK = 'uk', // Ukrainian
  TR = 'tr'  // Turkish
}

export interface TranslationContent {
  language: SupportedLanguage;
  unitCommercialDescription: string;
}

export interface UnitTranslation {
  id?: number;
  unitId: string;
  translations: TranslationContent[];
  createdAt?: Date;
  updatedAt?: Date;
}

// EPC Types
export interface EPC {
  id: number;
  epcLabel: string;
  kWh: string;
  uValueWalls: number | null;
  uValueGlazing: number | null;
  uValueWindows: number | null;
  fileLogId: number;
  fileLog?: {
    id: number;
    documentDate: Date;
    sharePointFileUrl?: string;
  };
}

export interface EnergyPerformanceInfo {
  id: number;
  epcs: EPC[];
}

export interface UnitCommercialInfoComponentProps {
  unit: UnitDTO;
  onUnitUpdate?: (updates: Partial<UnitDTO>) => void;
  isCompact?: boolean; // For use in tables vs full view
  onValidationErrors?: (errors: string[]) => void;
  isPublishing?: boolean;
}

// Visitation Contact Cell Component
const VisitationContactCell = ({ 
  unit, 
  onUpdate 
}: { 
  unit: UnitDTO,
  onUpdate: (contactData: any) => void
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const currentContact = unit.visitationResponsibleContact;
  const { session } = useSession();

  const handleSendEmail = async () => {
    if (!currentContact || !unit.id) return;
    
    setIsSendingEmail(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/send-visitation-responsible-notification/${unit.id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        },
        session
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      } else {
        toast.error('Fout bij verzenden e-mail');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Fout bij verzenden e-mail');
    } finally {
      setIsSendingEmail(false);
    }
  };

  if (isEditing) {
    return (
      <div className="w-full">
        <DirectSearchComponent
          label="Zoek contact"
          resourceFilter={[ResourceType.CONTACT]}
          mode={SearchMode.SEARCH_AND_REDIRECT}
          initialResource={
            currentContact 
              ? {
                  id: currentContact.id,
                  resourceId: currentContact.id?.toString(),
                  type: ResourceType.CONTACT,
                  name: `${currentContact.firstName || ''} ${currentContact.lastName || ''}`.trim(),
                  metadata1: currentContact.email || '',
                  metadata2: currentContact.phone_mobile || '',
                }
              : null
          }
          onSelect={(contact) => {
            if (contact && contact.resourceId) {
              onUpdate({
                id: parseInt(contact.resourceId),
                firstName: contact.name.split(' ')[0] || '',
                lastName: contact.name.split(' ').slice(1).join(' ') || '',
                email: contact.metadata1 || '',
                phone_mobile: contact.metadata2 || '',
              });
            }
            setIsEditing(false);
          }}
        />
        <div className="flex gap-2 mt-2">
          {currentContact && (
            <button
              onClick={() => {
                onUpdate(null);
                setIsEditing(false);
              }}
              className="px-3 py-1 text-sm text-red-600 hover:text-red-700 border border-red-200 rounded-sm hover:bg-red-50 transition-colors"
            >
              Verwijderen
            </button>
          )}
          <button
            onClick={() => setIsEditing(false)}
            className="px-3 py-1 text-sm border border-neutral-primary/20 rounded-sm hover:bg-gray-50 transition-colors"
          >
            Annuleren
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between group">
      <div className="text-sm">
        {currentContact ? (
          <div>
            <span className="text-neutral-primary font-medium">
              {currentContact.firstName} {currentContact.lastName}
            </span>
            {currentContact.email && (
              <p className="text-xs text-neutral-primary/70 mt-1">{currentContact.email}</p>
            )}
            {currentContact.phone_mobile && (
              <p className="text-xs text-neutral-primary/70">{currentContact.phone_mobile}</p>
            )}
          </div>
        ) : (
          <span className="text-neutral-primary/60 italic">Geen contact toegewezen</span>
        )}
      </div>
      <div className="flex items-center gap-1">
        {currentContact && (
          <button
            onClick={handleSendEmail}
            disabled={isSendingEmail}
            className="opacity-0 group-hover:opacity-100 p-2 hover:bg-moss-primary/10 rounded-sm transition-opacity text-moss-primary disabled:opacity-50"
            title="E-mail met kandidaten overzicht verzenden"
          >
            {isSendingEmail ? (
              <div className="animate-spin w-4 h-4 border-2 border-moss-primary border-t-transparent rounded-full"></div>
            ) : (
              <Envelope size={16} />
            )}
          </button>
        )}
        <button
          onClick={() => setIsEditing(true)}
          className="opacity-0 group-hover:opacity-100 p-2 hover:bg-gray-100 rounded-sm transition-opacity"
          title="Bewerken"
        >
          <PencilSimple size={16} />
        </button>
      </div>
    </div>
  );
};

export const UnitCommercialInfoComponent: React.FC<UnitCommercialInfoComponentProps> = ({
  unit,
  onUnitUpdate,
  isCompact = false,
  onValidationErrors,
  isPublishing = false
}) => {
  const { session } = useSession();
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<any>({});
  const [devices, setDevices] = useState<Device[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState<boolean>(true);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState<boolean>(false);
  const [isAddingDevice, setIsAddingDevice] = useState<boolean>(false);
  const [epcs, setEpcs] = useState<EPC[]>([]);
  const [isLoadingEpcs, setIsLoadingEpcs] = useState<boolean>(true);
  const [latestEpc, setLatestEpc] = useState<EPC | null>(null);
  const [isLoadingUnit, setIsLoadingUnit] = useState<boolean>(true);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [savingField, setSavingField] = useState<string | null>(null);
  const [savedField, setSavedField] = useState<string | null>(null);
  
  // Translation states
  const [unitTranslations, setUnitTranslations] = useState<UnitTranslation | null>(null);
  const [isTranslating, setIsTranslating] = useState<boolean>(false);

  // Required fields for calendar publication
  const requiredFieldsForPublication = [
    'advertisedRentalPrice',
    'advertisedProvisions', 
    'inspectionNumBedrooms',
    'inspectionNumBathrooms',
    'unitCommercialDescription'
  ];

  const validateRequiredFields = () => {
    const errors: Record<string, string> = {};
    
    requiredFieldsForPublication.forEach(field => {
      const value = (unit as any)[field];
      if (value == null || (typeof value === 'string' && value.trim() === '')) {
        switch (field) {
          case 'advertisedRentalPrice':
            errors[field] = 'Huurprijs is verplicht voor publicatie';
            break;
          case 'advertisedProvisions':
            errors[field] = 'Provisies zijn verplicht voor publicatie';
            break;
          case 'inspectionNumBedrooms':
            errors[field] = 'Aantal slaapkamers is verplicht voor publicatie';
            break;
          case 'inspectionNumBathrooms':
            errors[field] = 'Aantal badkamers is verplicht voor publicatie';
            break;
          case 'unitCommercialDescription':
            errors[field] = 'Commerciële beschrijving is verplicht voor publicatie';
            break;
        }
      }
    });
    
    setValidationErrors(errors);
    if (onValidationErrors) {
      onValidationErrors(Object.values(errors));
    }
    
    return Object.keys(errors).length === 0;
  };

  // Validate when publishing state changes
  useEffect(() => {
    if (isPublishing) {
      validateRequiredFields();
    }
  }, [isPublishing, unit]);

  // Check if translations exist
  const hasTranslations = () => {
    return unitTranslations?.translations && unitTranslations.translations.length > 0;
  };

  // Handle description save with translation cleanup
  const handleDescriptionSave = async (field: string, newDescription: string) => {
    // If description changed, delete existing translations
    if (newDescription !== unit.unitCommercialDescription && hasTranslations()) {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/units/${unit.id}/translations`,
          { method: 'DELETE' },
          session
        );
        if (response.ok) {
          setUnitTranslations(null);
          console.log('✅ Translations deleted due to description change');
        }
      } catch (error) {
        console.error('Error deleting translations:', error);
      }
    }
    
    // Call the original save handler
    return await handleSave(field, newDescription);
  };

  // Handle translate button click
  const handleTranslateClick = async () => {
    if (!unit?.id || !unit.unitCommercialDescription?.trim()) {
      toast.error('Geen beschrijving om te vertalen');
      return;
    }

    setIsTranslating(true);
    
    try {
      const response = await fetchWithAuth(
        `${API_URL}/units/${unit.id}/translate-commercial-description`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        },
        session
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Update local state with the translations
          setUnitTranslations({
            id: Date.now(), // temporary ID
            unitId: unit.id,
            translations: result.translations,
            createdAt: new Date(),
            updatedAt: new Date()
          });
          
          toast.success(result.message);
          console.log('✅ Translations saved:', result.translations);
        } else {
          toast.error(result.message || 'Vertaling mislukt');
        }
      } else {
        toast.error('Fout bij vertalen');
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('Fout bij vertalen');
    } finally {
      setIsTranslating(false);
    }
  };

  // Centralized, robust save function that works for ALL field types
  const handleSave = async (field: string, value: any) => {
    try {
      setSavingField(field);
      console.log('🚀 UNIFIED SAVE STARTED');
      console.log('Field:', field);
      console.log('Raw value:', value);
      console.log('Value type:', typeof value);
      console.log('Is required field:', requiredFieldsForPublication.includes(field));
      
      // Step 1: Value preprocessing and validation
      let processedValue = value;
      
      // Handle empty values
      const isEmpty = value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

      if (isEmpty) {
        if (requiredFieldsForPublication.includes(field)) {
          console.log('❌ Required field cannot be empty');
          toast.error('Dit veld is verplicht');
          setSavingField(null);
          return;
        }
        processedValue = null; // Save as null for optional empty fields
        console.log('✅ Saving empty optional field as null');
      } else {
        // Process non-empty values
        if (typeof value === 'string') {
          const trimmed = value.trim();

          // Handle boolean field conversion (furnished)
          if (field === 'furnished') {
            if (trimmed === 'Ja') {
              processedValue = true;
            } else if (trimmed === 'Nee') {
              processedValue = false;
            } else {
              processedValue = null; // Onbekend
            }
            console.log('✅ Converting furnished string to boolean:', processedValue);
          }
          // For number fields, try to parse
          else if (['grossSurface', 'netSurface', 'advertisedRentalPrice', 'advertisedProvisions',
               'inspectionNumBedrooms', 'inspectionNumBathrooms', 'inspectionNumToilets',
               'unitMaxCapacityPersons', 'outsideSpaceSurface'].includes(field)) {
            // Een komma als decimaalteken (numeriek toetsenblok) mag niet stil
            // afgekapt worden: parseFloat('1,5') geeft 1.
            const numValue = parseFloat(trimmed.replace(',', '.'));
            if (isNaN(numValue)) {
              console.log('❌ Invalid number value');
              toast.error('Voer een geldig getal in');
              setSavingField(null);
              return;
            }
            processedValue = numValue;
          } else {
            processedValue = trimmed;
          }
        }
      }
      
      console.log('Processed value:', processedValue);
      
      // Step 2: Determine endpoint and payload
      const inspectionFields = ['inspectionNumBedrooms', 'inspectionNumBathrooms', 'inspectionNumToilets', 'inspectionGeneralRemarks'];
      const conformityFields = ['unitMaxCapacityPersons'];
      
      let endpoint;
      let payload;
      
      if (inspectionFields.includes(field)) {
        endpoint = `${API_URL}/units/inspection-info/${unit.id}`;
        const fieldMap: Record<string, string> = {
          'inspectionNumBedrooms': 'numBedrooms',
          'inspectionNumBathrooms': 'numBathrooms', 
          'inspectionNumToilets': 'numToilets',
          'inspectionGeneralRemarks': 'generalRemarks'
        };
        payload = {
          unitId: unit.id,
          [fieldMap[field] || field]: processedValue
        };
        console.log('📍 Using inspection info endpoint');
      } else if (conformityFields.includes(field)) {
        endpoint = `${API_URL}/units/conformity-info/${unit.id}`;
        payload = {
          unitId: unit.id,
          [field]: processedValue
        };
        console.log('📍 Using conformity info endpoint');
      } else {
        endpoint = `${API_URL}/units/update/${unit.id}`;
        payload = {
          id: unit.id,
          [field]: processedValue
        };
        console.log('📍 Using main unit update endpoint');
      }
      
      console.log('Endpoint:', endpoint);
      console.log('Payload:', JSON.stringify(payload));
      
      // Step 3: Make API call
      const response = await fetchWithAuth(
        endpoint,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        },
        session
      );
      
      console.log('API Response status:', response?.status);
      
      if (!response?.ok) {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        throw new Error(`Save failed: ${response.status} ${errorText}`);
      }
      
      // Step 4: Update local state and UI
      console.log('✅ API call successful, updating UI');
      
      // Immediately update local state
      if (onUnitUpdate) {
        onUnitUpdate({ [field]: processedValue });
      }
      
      // Clear validation errors
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
      
      // Close edit mode and show success
      setEditingField(null);
      setEditValues({});
      setSavingField(null);
      setSavedField(field);
      
      // Clear success indicator after 2 seconds
      setTimeout(() => setSavedField(null), 2000);
      
      console.log('🎉 SAVE COMPLETED SUCCESSFULLY');
      
    } catch (error) {
      console.error('💥 SAVE FAILED:', error);
      setSavingField(null);
      // De ruwe statuscode en JSON hoorden in een browser-alert niet thuis: naar
      // Sentry sturen voor debugging, de gebruiker krijgt een leesbare melding.
      Sentry.captureException(error, {
        tags: { module: 'unit', component: 'UnitCommercialInfoComponent' },
        extra: { unitId: unit?.id, field, value },
      });
      toast.error(
        `Opslaan van "${field}" is mislukt. Probeer het opnieuw of meld dit als de fout blijft terugkomen.`,
      );
    }
  };

  const startEditing = (field: string, currentValue: any) => {
    setEditingField(field);
    // `??` (not `||`): a stored 0 (e.g. 0 m² buitenruimte) must open with "0" in
    // the input. With `||` it opened empty, and confirming that empty input
    // saved the field as null — silently turning "0 m²" back into "-".
    setEditValues({ [field]: currentValue ?? '' });
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValues({});
  };

  // Fetch devices for the unit
  const fetchDevices = async () => {
    if (!unit?.id) {
      setIsLoadingDevices(false);
      return;
    }

    try {
      setIsLoadingDevices(true);
      const response = await fetchWithAuth(
        `${API_URL}/devices/${unit.id}`,
        { method: "GET" },
        session,
      );
      const deviceData = await response.json();
      setDevices(deviceData || []);
    } catch (error) {
      console.error('Failed to fetch devices:', error);
      setDevices([]);
    } finally {
      setIsLoadingDevices(false);
    }
  };

  // Fetch EPCs for the unit using file logs approach like in energy performance section
  const fetchEpcs = async () => {
    if (!unit?.id) {
      setIsLoadingEpcs(false);
      return;
    }

    try {
      setIsLoadingEpcs(true);

      const queryParams = new URLSearchParams();

      const acceptedLabels = ['EPC unit'];
 
    if (unit?.id) {
        queryParams.append('unitId', unit.id);
    }
    if (acceptedLabels.length > 0) {
        queryParams.append('labels', acceptedLabels.join(','));
    }
      
      // First, fetch file logs for this unit that have EPC label
      const fileLogsResponse = await fetchWithAuth(
        `${API_URL}/file/get-file-logs?${queryParams.toString()}`,
        { method: "GET" },
        session,
      );
      
      if (!fileLogsResponse.ok) {
        setEpcs([]);
        setLatestEpc(null);
        return;
      }
      
      const fileLogs = await fileLogsResponse.json();
      console.log('File logs fetched for unit:', unit.id, fileLogs);
      
      // Filter for EPC unit file logs (label contains "EPC unit")
      const epcFileLogs = fileLogs.filter((fileLog: any) => {
        const hasEpcLabel = fileLog.label && 
          Array.isArray(fileLog.label) && 
          fileLog.label.some((label: string) => label.toLowerCase().includes('epc'));
        console.log('File log', fileLog.id, 'labels:', fileLog.label, 'hasEpcLabel:', hasEpcLabel);
        return hasEpcLabel;
      });

      console.log('EPC file logs found:', epcFileLogs.length, epcFileLogs);

      if (epcFileLogs.length === 0) {
        setEpcs([]);
        setLatestEpc(null);
        return;
      }

      // Fetch EPC entities for each file log
      const epcPromises = epcFileLogs.map(async (fileLog: any) => {
        try {
          const epcResponse = await fetchWithAuth(
            `${API_URL}/file/file-log-with-epc/${fileLog.id}`,
            { method: "GET" },
            session,
          );
          
          if (epcResponse.ok) {
            const fileLogWithEpc = await epcResponse.json();
            return {
              id: fileLogWithEpc.epc?.id || fileLog.id,
              epcLabel: fileLogWithEpc.epc?.epcLabel || 'Onbekend',
              kWh: fileLogWithEpc.epc?.kWh || '',
              uValueWalls: fileLogWithEpc.epc?.uValueWalls || null,
              uValueGlazing: fileLogWithEpc.epc?.uValueGlazing || null,
              uValueWindows: fileLogWithEpc.epc?.uValueWindows || null,
              fileLogId: fileLog.id,
              fileLog: {
                id: fileLog.id,
                documentDate: fileLog.documentDate,
                sharePointFileUrl: fileLog.sharePointFileUrl,
              }
            };
          }
        } catch (error) {
          console.warn(`Failed to fetch EPC for file log ${fileLog.id}:`, error);
          // Return basic structure even if EPC entity doesn't exist
          return {
            id: fileLog.id,
            epcLabel: 'Onbekend',
            kWh: '',
            uValueWalls: null,
            uValueGlazing: null,
            uValueWindows: null,
            fileLogId: fileLog.id,
            fileLog: {
              id: fileLog.id,
              documentDate: fileLog.documentDate,
              sharePointFileUrl: fileLog.sharePointFileUrl,
            }
          };
        }
        return null;
      });

      const epcResults = await Promise.all(epcPromises);
      const validEpcs = epcResults.filter(epc => epc !== null);
      
      setEpcs(validEpcs);
      
      // Find the latest EPC based on documentDate
      if (validEpcs.length > 0) {
        const sortedEpcs = validEpcs
          .filter((epc: EPC) => epc.fileLog?.documentDate)
          .sort((a: EPC, b: EPC) => 
            new Date(b.fileLog!.documentDate).getTime() - new Date(a.fileLog!.documentDate).getTime()
          );
        
        setLatestEpc(sortedEpcs.length > 0 ? sortedEpcs[0] : validEpcs[0]);
      } else {
        setLatestEpc(null);
      }
      
    } catch (error) {
      console.error('Failed to fetch EPCs:', error);
      setEpcs([]);
      setLatestEpc(null);
    } finally {
      setIsLoadingEpcs(false);
    }
  };

  // Fetch existing translations for the unit
  const fetchExistingTranslations = async () => {
    if (!unit?.id) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/units/${unit.id}/translations`,
        { method: 'GET' },
        session
      );

      if (response.ok) {
        const result = await response.json();
        if (result.translations && result.translations.length > 0) {
          setUnitTranslations({
            id: result.id || Date.now(),
            unitId: unit.id,
            translations: result.translations,
            createdAt: new Date(),
            updatedAt: new Date()
          });
          console.log(`🌐 Loaded existing ${result.translations.length} translations for unit ${unit.id}`);
        }
      }
    } catch (error) {
      console.error('Error fetching existing translations:', error);
    }
  };

  // Load devices, EPCs, and translations when component mounts or unit changes
  useEffect(() => {
    fetchDevices();
    fetchEpcs();
    fetchExistingTranslations();
  }, [unit?.id]);


  // Set loading state based on unit data availability
  useEffect(() => {
    // Unit is considered loaded when we have basic unit data
    if (unit?.id) {
      // Add a small delay to ensure smooth loading state transition
      const timer = setTimeout(() => {
        setIsLoadingUnit(false);
      }, 200);
      return () => clearTimeout(timer);
    } else {
      setIsLoadingUnit(true);
    }
  }, [unit?.id]);

  // Device type icon mapping
  const getDeviceIcon = (deviceType: string | null) => {
    if (!deviceType) return <DeviceMobile size={16} className="text-neutral-primary/60" />;
    
    switch (deviceType) {
      case DeviceType.OVEN:
      case DeviceType.COMBI_OVEN:
        return <Oven size={16} className="text-neutral-primary/60" />;
      case DeviceType.MICROWAVE:
        return <Lightning size={16} className="text-neutral-primary/60" />;
      case DeviceType.REFRIGERATOR_WITH_FREEZER:
      case DeviceType.REFRIGERATOR_NO_FREEZER:
        return <ThermometerCold size={16} className="text-neutral-primary/60" />;
      case DeviceType.DISHWASHER:
        return <Drop size={16} className="text-neutral-primary/60" />;
      case DeviceType.HOB_INDUCTION:
      case DeviceType.HOB_ELECTRICAL:
      case DeviceType.HOB_CERAMIC:
      case DeviceType.HOB_GAS:
      case DeviceType.HOB_INDUCTION_WITH_EXTRACTOR_HOOD:
        return <CookingPot size={16} className="text-neutral-primary/60" />;
      case DeviceType.WASHMACHINE:
        return <WashingMachine size={16} className="text-neutral-primary/60" />;
      case DeviceType.DRYER:
        return <Snowflake size={16} className="text-neutral-primary/60" />;
      case DeviceType.EXTRACTOR_HOOD:
        return <ForkKnife size={16} className="text-neutral-primary/60" />;
      default:
        return <DeviceMobile size={16} className="text-neutral-primary/60" />;
    }
  };

  // Device modal handlers
  const handleOpenDeviceModal = (device: Device) => {
    setSelectedDevice(device);
    setIsDeviceModalOpen(true);
    setIsAddingDevice(false);
  };

  const handleOpenAddDeviceModal = () => {
    setSelectedDevice(null);
    setIsDeviceModalOpen(true);
    setIsAddingDevice(true);
  };

  const handleCloseDeviceModal = () => {
    setIsDeviceModalOpen(false);
    setSelectedDevice(null);
    setIsAddingDevice(false);
    // Refresh devices after modal closes
    fetchDevices();
  };

  // EPC utility functions
  const getEpcLabelColor = (epcLabel: string) => {
    const label = epcLabel?.toLowerCase();
    switch (label) {
      case 'a+':
      case 'a':
        return 'bg-status-good text-white';
      case 'b':
      case 'c':
        return 'bg-moss-primary text-white';
      case 'd':
      case 'e':
        return 'bg-yellow-500 text-white';
      case 'f':
      case 'g':
        return 'bg-brick-primary text-white';
      default:
        return 'bg-neutral-primary/20 text-neutral-primary';
    }
  };

  const formatEpcDate = (dateString: string | Date) => {
    if (!dateString) return 'Onbekend';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-BE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

    const getStatusIcon = (value: any, type: 'available' | 'yesno' | 'remote') => {
    let isPositive = false;

    if (type === 'available') {
      isPositive = value === 'Beschikbaar';
    } else if (type === 'yesno') {
      isPositive = value === 'Ja';
    } else if (type === 'remote') {
      isPositive = value === 'Videofoon' || value === 'Analoge toegang';
    }

    const isNegative = (type === 'available' && value === 'Niet beschikbaar') ||
                     (type === 'yesno' && value === 'Nee') ||
                     (type === 'remote' && value === 'Geen toegang op afstand mogelijk');

    if (isPositive) {
      return <Check size={16} weight="bold" className="text-status-good" />;
    } else if (isNegative) {
      return <X size={16} weight="bold" className="text-brick-primary" />;
    } else {
      return <Question size={16} weight="bold" className="text-neutral-secondary-mid" />;
    }
  };

  const getStatusColor = (value: any, type: 'available' | 'yesno' | 'remote'): string => {
    let isPositive = false;

    if (type === 'available') {
      isPositive = value === 'Beschikbaar';
    } else if (type === 'yesno') {
      isPositive = value === 'Ja';
    } else if (type === 'remote') {
      isPositive = value === 'Videofoon' || value === 'Analoge toegang';
    }

    const isNegative = (type === 'available' && value === 'Niet beschikbaar') ||
                       (type === 'yesno' && value === 'Nee') ||
                       (type === 'remote' && value === 'Geen toegang op afstand mogelijk');

    if (isPositive) return 'text-status-good';
    if (isNegative) return 'text-brick-primary';
    return 'text-neutral-secondary-mid';
  };

  const StatusField = ({ 
    icon: Icon, 
    label, 
    field, 
    value, 
    options, 
    type,
    isLoading = false
  }: {
    icon: React.ComponentType<any>,
    label: string,
    field: string,
    value: any,
    options: { value: string, label: string }[],
    type: 'available' | 'yesno' | 'remote',
    isLoading?: boolean
  }) => {
    const isEditing = editingField === field;
    
    return (
      <div className="flex items-center justify-between p-2 border-b border-neutral-primary/10 last:border-b-0">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-neutral-primary/60" />
          <span className="text-sm font-medium text-neutral-primary">{label}</span>
        </div>
        
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-moss-primary border-t-transparent rounded-full animate-spin"></div>
              ) : (
                getStatusIcon(value, type)
              )}
              <span className="text-sm text-neutral-primary min-w-[100px] text-right">
                {isLoading ? 'Laden...' : (value || 'Onbekend')}
              </span>
              <button
                onClick={() => startEditing(field, value)}
                className="text-neutral-primary/40 hover:text-moss-primary transition-colors"
                disabled={isLoading}
              >
                <PencilSimple size={16} />
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <select
                value={editValues[field] || ''}
                onChange={(e) => setEditValues({ ...editValues, [field]: e.target.value })}
                className="border border-neutral-primary/20 rounded-sm px-2 py-1 text-sm focus:outline-hidden focus:border-moss-primary"
                style={{ minWidth: '120px' }}
              >
                {options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={savingField === field}
                onClick={(e) => {
                  e.preventDefault();
                  console.log(`🔥 FIELD SAVE CLICKED - Field: ${field}, Value: "${editValues[field]}"`);
                  handleSave(field, editValues[field]);
                }}
                className={`transition-colors ${
                  savingField === field 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-status-good hover:text-status-good/80'
                }`}
              >
                {savingField === field ? (
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Check size={16} weight="bold" />
                )}
              </button>
              <button
                onClick={cancelEditing}
                className="text-brick-primary hover:text-brick-primary/80 transition-colors"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };



  const NumberField = ({ 
    icon: Icon, 
    label, 
    field, 
    value,
    mask 
  }: {
    icon: React.ComponentType<any>,
    label: string,
    field: string,
    value: any,
    mask?: 'surface' | 'valuta'
  }) => {
    const isEditing = editingField === field;
    
    return (
      <div className="flex items-center justify-between p-2 border-b border-neutral-primary/10 last:border-b-0">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-neutral-primary/60" />
          <span className="text-sm font-medium text-neutral-primary">{label}</span>
        </div>
        
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <span className="text-sm text-neutral-primary min-w-[80px] text-right">
                {/* Guard against masking an empty value ("undefined m²") */}
                {value === null || value === undefined || value === ''
                  ? 'N/A'
                  : mask
                    ? applyMask(value, mask)
                    : value}
              </span>
              <button
                onClick={() => startEditing(field, value)}
                className="text-neutral-primary/40 hover:text-moss-primary transition-colors"
              >
                <PencilSimple size={16} />
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.01"
                /* `??`: keeps a numeric 0 visible in the input instead of
                   blanking it (which would then be saved as null). */
                value={editValues[field] ?? ''}
                onChange={(e) => setEditValues({ ...editValues, [field]: e.target.value })}
                className="border border-neutral-primary/20 rounded-sm px-2 py-1 text-sm focus:outline-hidden focus:border-moss-primary w-20 text-right"
              />
              <button
                type="button"
                disabled={savingField === field}
                onClick={(e) => {
                  e.preventDefault();
                  console.log(`🔥 OLD NUMBER FIELD SAVE CLICKED - Field: ${field}, Value: "${editValues[field]}"`);
                  handleSave(field, editValues[field]); // Let unified save handle the parsing
                }}
                className={`transition-colors ${
                  savingField === field 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-status-good hover:text-status-good/80'
                }`}
              >
                {savingField === field ? (
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Check size={16} weight="bold" />
                )}
              </button>
              <button
                onClick={cancelEditing}
                className="text-brick-primary hover:text-brick-primary/80 transition-colors"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Handle Immoweb reference ID operations
  const handleImmowebReferenceChange = async (referenceId: number | null) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/units/${unit.id}/reference-id`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ referenceId }),
        },
        session
      );

      if (response.ok) {
        const updatedUnit = await response.json();
        onUnitUpdate?.({
          immowebReferenceId: updatedUnit.immowebReferenceId,
          immowebReferenceIdAddedTimestamp: updatedUnit.immowebReferenceIdAddedTimestamp,
        });
        
        if (referenceId) {
          console.log('Immoweb referentie succesvol bijgewerkt!');
        } else {
          console.log('Immoweb referentie succesvol verwijderd!');
        }
      }
    } catch (error) {
      console.error('Error updating Immoweb reference:', error);
    }
  };

  const ImmowebReferenceField = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const currentReferenceId = (unit as any).immowebReferenceId;
    const addedTimestamp = (unit as any).immowebReferenceIdAddedTimestamp;

    const handleEdit = () => {
      setEditValue(currentReferenceId?.toString() || '');
      setIsEditing(true);
    };

    const handleSave = async () => {
      setIsLoading(true);
      const newValue = editValue.trim() ? parseInt(editValue.trim()) : null;
      
      if (newValue && (isNaN(newValue) || newValue <= 0)) {
        console.error('Ongeldige referentie-ID');
        setIsLoading(false);
        return;
      }

      await handleImmowebReferenceChange(newValue);
      setIsEditing(false);
      setIsLoading(false);
    };

    const handleCancel = () => {
      setIsEditing(false);
      setEditValue('');
    };

    const handleDelete = async () => {
      if (window.confirm('Weet je zeker dat je de Immoweb referentie wilt verwijderen?')) {
        setIsLoading(true);
        await handleImmowebReferenceChange(null);
        setIsLoading(false);
      }
    };

    const formatTimestamp = (timestamp: string | Date) => {
      if (!timestamp) return '';
      return new Date(timestamp).toLocaleDateString('nl-NL', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    return (
      <div className="p-3 border-t border-neutral-primary/10">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Globe size={16} className="text-neutral-primary" />
            <span className="text-sm font-medium text-neutral-primary">Immoweb Referentie</span>
          </div>
          {!isEditing && currentReferenceId && (
            <div className="flex items-center gap-2">
              <a 
                href={`https://www.immoweb.be/nl/zoekertje/${currentReferenceId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-moss-primary hover:text-moss-primary/80 transition-colors"
                title="Open op Immoweb"
              >
                <LinkSimple size={16} />
              </a>
              <button 
                onClick={handleEdit}
                className="text-neutral-primary/60 hover:text-neutral-primary transition-colors"
                title="Bewerken"
                disabled={isLoading}
              >
                <PencilSimple size={16} />
              </button>
              <button 
                onClick={handleDelete}
                className="text-red-500 hover:text-red-600 transition-colors"
                title="Verwijderen"
                disabled={isLoading}
              >
                <Trash size={16} />
              </button>
            </div>
          )}
          {!isEditing && !currentReferenceId && (
            <button 
              onClick={handleEdit}
              className="text-moss-primary hover:text-moss-primary/80 text-sm"
              disabled={isLoading}
            >
              + Toevoegen
            </button>
          )}
        </div>

        {isEditing ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              placeholder="Voer referentie-ID in..."
              className="flex-1 px-3 py-2 text-sm border border-neutral-primary/20 rounded-md focus:outline-hidden focus:border-moss-primary"
              disabled={isLoading}
            />
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="px-3 py-2 text-sm bg-moss-primary text-white rounded-md hover:bg-moss-primary/90 transition-colors disabled:opacity-50"
            >
              {isLoading ? '...' : 'Opslaan'}
            </button>
            <button
              onClick={handleCancel}
              disabled={isLoading}
              className="px-3 py-2 text-sm border border-neutral-primary/20 rounded-md hover:bg-neutral-primary/5 transition-colors"
            >
              Annuleren
            </button>
          </div>
        ) : (
          <div className="text-sm text-neutral-primary/80">
            {currentReferenceId ? (
              <div className="space-y-1">
                <div className="font-medium">#{currentReferenceId}</div>
                {addedTimestamp && (
                  <div className="text-xs text-neutral-primary/60">
                    Toegevoegd: {formatTimestamp(addedTimestamp)}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-neutral-primary/60 italic">Geen referentie ingesteld</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const SelectField = ({ 
    icon: Icon, 
    label, 
    field, 
    value, 
    options 
  }: {
    icon: React.ComponentType<any>,
    label: string,
    field: string,
    value: any,
    options: { value: string, label: string }[]
  }) => {
    const isEditing = editingField === field;
    
    return (
      <div className="flex items-center justify-between p-2 border-b border-neutral-primary/10 last:border-b-0">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-neutral-primary/60" />
          <span className="text-sm font-medium text-neutral-primary">{label}</span>
        </div>
        
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <span className="text-sm text-neutral-primary min-w-[100px] text-right">
                {value || 'Niet opgegeven'}
              </span>
              <button
                onClick={() => startEditing(field, value)}
                className="text-neutral-primary/40 hover:text-moss-primary transition-colors"
              >
                <PencilSimple size={16} />
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <select
                value={editValues[field] || ''}
                onChange={(e) => setEditValues({ ...editValues, [field]: e.target.value })}
                className="border border-neutral-primary/20 rounded-sm px-2 py-1 text-sm focus:outline-hidden focus:border-moss-primary"
                style={{ minWidth: '120px' }}
              >
                <option value="">Selecteer...</option>
                {options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={savingField === field}
                onClick={(e) => {
                  e.preventDefault();
                  console.log(`🔥 FIELD SAVE CLICKED - Field: ${field}, Value: "${editValues[field]}"`);
                  handleSave(field, editValues[field]);
                }}
                className={`transition-colors ${
                  savingField === field 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-status-good hover:text-status-good/80'
                }`}
              >
                {savingField === field ? (
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Check size={16} weight="bold" />
                )}
              </button>
              <button
                onClick={cancelEditing}
                className="text-brick-primary hover:text-brick-primary/80 transition-colors"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const DevicesTable = () => {
    if (isLoadingDevices) {
      return (
        <div className="p-3 text-center text-neutral-primary/60">
          <div className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-moss-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="text-xs">Apparaten laden...</p>
          </div>
        </div>
      );
    }

    if (devices.length === 0) {
      return (
        <div className="p-4 text-center text-neutral-primary/60">
          <DeviceMobile size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm mb-3">Geen apparaten toegevoegd</p>
          <button
            onClick={handleOpenAddDeviceModal}
            className="flex items-center gap-2 mx-auto px-4 py-2 bg-moss-primary text-white rounded-md text-sm hover:bg-moss-primary/90 transition-colors"
          >
            <Plus size={16} />
            Apparaat toevoegen
          </button>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm text-neutral-primary/80">{devices.length} apparaat{devices.length !== 1 ? 'en' : ''}</span>
          <button
            onClick={handleOpenAddDeviceModal}
            className="flex items-center gap-1 px-3 py-1.5 bg-moss-primary text-white rounded-md text-sm hover:bg-moss-primary/90 transition-colors"
          >
            <Plus size={14} />
            Toevoegen
          </button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-primary/10">
              <th className="text-left py-2 px-3 font-medium text-neutral-primary">Type</th>
              <th className="text-left py-2 px-3 font-medium text-neutral-primary">Merk</th>
              <th className="text-left py-2 px-3 font-medium text-neutral-primary">Model</th>
              <th className="text-left py-2 px-3 font-medium text-neutral-primary">Energielabel</th>
              <th className="text-left py-2 px-3 font-medium text-neutral-primary">Acties</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device, index) => (
              <tr key={device.id || index} className="border-b border-neutral-primary/5 hover:bg-neutral-primary/5">
                <td className="py-2 px-3">
                  <div className="flex items-center gap-2">
                    {getDeviceIcon(device.deviceType)}
                    <span>{device.deviceType || 'Onbekend'}</span>
                  </div>
                </td>
                <td className="py-2 px-3 text-neutral-primary/80">{device.brand || '-'}</td>
                <td className="py-2 px-3 text-neutral-primary/80">{device.model || '-'}</td>
                <td className="py-2 px-3">
                  {device.energyLabel ? (
                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                      device.energyLabel === 'A+' || device.energyLabel === 'A' 
                        ? 'bg-status-good text-white' 
                        : device.energyLabel === 'B' || device.energyLabel === 'C'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-brick-primary text-white'
                    }`}>
                      {device.energyLabel}
                    </span>
                  ) : (
                    <span className="text-neutral-primary/40">-</span>
                  )}
                </td>
                <td className="py-2 px-3">
                  <button
                    onClick={() => handleOpenDeviceModal(device)}
                    className="text-moss-primary hover:text-moss-primary/80 transition-colors"
                    title="Apparaat bewerken"
                  >
                    <Eye size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (isCompact) {
    // Compact version for table cells
    return (
      <div className="flex items-center gap-1.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex items-center cursor-default">
              <WifiHigh size={16} className={getStatusColor(unit.wifiAvailable, 'available')} />
            </span>
          </TooltipTrigger>
          <TooltipContent>Wifi: {unit.wifiAvailable || 'Onbekend'}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex items-center cursor-default">
              <WashingMachine size={16} className={getStatusColor(unit.washingMachineAvailable, 'available')} />
            </span>
          </TooltipTrigger>
          <TooltipContent>Wasmachine: {unit.washingMachineAvailable || 'Onbekend'}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex items-center cursor-default">
              <Bathtub size={16} className={getStatusColor(unit.bathAvailable, 'available')} />
            </span>
          </TooltipTrigger>
          <TooltipContent>Bad: {unit.bathAvailable || 'Onbekend'}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex items-center cursor-default">
              <Shower size={16} className={getStatusColor(unit.showerAvailable, 'available')} />
            </span>
          </TooltipTrigger>
          <TooltipContent>Douche: {unit.showerAvailable || 'Onbekend'}</TooltipContent>
        </Tooltip>
        {unit.outsideSpace && unit.outsideSpace !== 'Geen Buitenruimte' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center cursor-default">
                <TreePalm size={16} className="text-status-good" />
              </span>
            </TooltipTrigger>
            <TooltipContent>Buitenruimte: {unit.outsideSpace}</TooltipContent>
          </Tooltip>
        )}
      </div>
    );
  }

      return (
      <div className="bg-white rounded-lg border border-neutral-primary/15 overflow-hidden">
        <div className="px-4 py-3 bg-moss-primary/5 border-b border-neutral-primary/10">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-neutral-primary flex items-center gap-2">
              <Buildings size={20} />
              Publieke informatie
            </h3>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
              <Info size={16} className="text-blue-600" />
              <span className="text-xs text-blue-700 font-medium">
                Zichtbaar op Yally kalender/verhuur website/Immoweb
              </span>
            </div>
          </div>
        </div>

        <div className="divide-y divide-neutral-primary/10">
          {/* PROMINENT: Commercial Description - Most Important */}
          <div className="p-0">
            <div className="p-4 border-b border-neutral-primary/10">
              {/* Header with translate button */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <SidebarSimple size={18} className="text-neutral-primary/60" />
                  <span className="text-sm font-medium text-neutral-primary">
                    Commerciële beschrijving
                  </span>
                  <span className="text-red-500">*</span>
                </div>
                
                <div className="flex items-center gap-3">
                  {/* Translate Button */}
                  <button
                    onClick={handleTranslateClick}
                    disabled={isTranslating || !unit.unitCommercialDescription?.trim() || hasTranslations()}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-moss-primary text-white rounded-md hover:bg-moss-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Vertaal naar alle talen"
                  >
                    {isTranslating ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Vertalen...
                      </>
                    ) : (
                      <>
                        <Translate size={14} />
                        Vertalen
                      </>
                    )}
                  </button>
                  
                  {/* Helper text */}
                  <span className="text-xs text-neutral-primary/60">
                    {hasTranslations() 
                      ? 'Deze beschrijving is al vertaald' 
                      : 'Alleen vertalen als het de finale versie is'
                    }
                  </span>
                </div>
              </div>
              
              {/* Description Field - Always shows Dutch version */}
              <div className="space-y-2">
                <div className="pl-0">
                  <EditableTextField
                    icon={SidebarSimple}
                    label=""
                    field="unitCommercialDescription"
                    value={unit.unitCommercialDescription}
                    isLoading={isLoadingUnit}
                    isRequired={true}
                    isProminentDescription={false}
                    isTextarea={true}
                    onSave={handleDescriptionSave}
                    isSaving={savingField === 'unitCommercialDescription'}
                    showSuccess={savedField === 'unitCommercialDescription'}
                    validationError={validationErrors.unitCommercialDescription}
                  />
                </div>
                
                {/* Translation Status */}
                <div className="text-xs text-neutral-primary/60 flex items-center gap-2">
                  <span>🇳🇱 Nederlandse versie</span>
                  {hasTranslations() && (
                    <span className="flex items-center gap-1 text-status-good">
                      <Check size={12} />
                      Vertalingen beschikbaar
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Rental and Sale Status Section */}
          <div className="p-3 bg-brick-secondary-fine">
            <h4 className="font-medium text-neutral-primary mb-3 flex items-center gap-2">
              <CalendarBlank size={16} />
              Status & Beschikbaarheid
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-0">
                <EditableSelectField
                  icon={Buildings}
                  label="Verhuurstatus"
                  field="rentStatus"
                  value={(unit as any).rentStatus}
                  options={[
                    { value: '', label: 'Selecteer status...' },
                    { value: 'Te huur', label: 'Te huur' },
                    { value: 'Verhuurd', label: 'Verhuurd' },
                    { value: 'Niet te huur', label: 'Niet te huur' }
                  ]}
                  isLoading={isLoadingUnit}
                  onSave={handleSave}
                  isSaving={savingField === 'rentStatus'}
                  showSuccess={savedField === 'rentStatus'}
                />
              </div>
              <div className="space-y-0">
                <EditableSelectField
                  icon={CurrencyEur}
                  label="Verkoopstatus"
                  field="saleStatus"
                  value={(unit as any).saleStatus}
                  options={[
                    { value: '', label: 'Selecteer status...' },
                    { value: 'Te koop', label: 'Te koop' },
                    { value: 'Verkocht', label: 'Verkocht' },
                    { value: 'Niet te koop', label: 'Niet te koop' }
                  ]}
                  isLoading={isLoadingUnit}
                  onSave={handleSave}
                  isSaving={savingField === 'saleStatus'}
                  showSuccess={savedField === 'saleStatus'}
                />
              </div>
            </div>
          </div>

          {/* Pricing Section */}
          <div className="p-3">
            <h4 className="font-medium text-neutral-primary mb-3 flex items-center gap-2">
              <CurrencyEur size={16} />
              Prijsinformatie
              <span className="text-red-500">*</span>
            </h4>
            <div className="space-y-0">
              <EditableNumberField
                icon={CurrencyEur}
                label="Geafficheerde huurprijs per maand (€)"
                field="advertisedRentalPrice"
                value={unit.advertisedRentalPrice}
                isLoading={isLoadingUnit}
                isRequired={true}
                onSave={handleSave}
                isSaving={savingField === 'advertisedRentalPrice'}
                showSuccess={savedField === 'advertisedRentalPrice'}
                validationError={validationErrors.advertisedRentalPrice}
              />
              <EditableNumberField
                icon={CurrencyEur}
                label="Geafficheerde provisies per maand (€)"
                field="advertisedProvisions"
                value={unit.advertisedProvisions}
                isLoading={isLoadingUnit}
                isRequired={true}
                onSave={handleSave}
                isSaving={savingField === 'advertisedProvisions'}
                showSuccess={savedField === 'advertisedProvisions'}
                validationError={validationErrors.advertisedProvisions}
              />
            </div>
          </div>

          {/* Property Details Section */}
          <div className="p-3">
            <h4 className="font-medium text-neutral-primary mb-3 flex items-center gap-2">
              <Bed size={16} />
              Ruimte Details
              <span className="text-red-500">*</span>
            </h4>
            <div className="space-y-0">
              <EditableDateField
                icon={CalendarBlank}
                label="Beschikbaar vanaf"
                field="availableFrom"
                value={unit.availableFrom}
                isLoading={isLoadingUnit}
                onSave={handleSave}
                isSaving={savingField === 'availableFrom'}
                showSuccess={savedField === 'availableFrom'}
              />
              <EditableNumberField
                icon={Users}
                label="Maximum aantal bewoners"
                field="unitMaxCapacityPersons"
                value={(unit as any).unitMaxCapacityPersons}
                isLoading={isLoadingUnit}
                onSave={handleSave}
                isSaving={savingField === 'unitMaxCapacityPersons'}
                showSuccess={savedField === 'unitMaxCapacityPersons'}
              />
              <EditableNumberField
                icon={Bed}
                label="Aantal slaapkamers"
                field="inspectionNumBedrooms"
                value={(unit as any).inspectionNumBedrooms}
                isLoading={isLoadingUnit}
                isRequired={true}
                onSave={handleSave}
                isSaving={savingField === 'inspectionNumBedrooms'}
                showSuccess={savedField === 'inspectionNumBedrooms'}
                validationError={validationErrors.inspectionNumBedrooms}
              />
              <EditableNumberField
                icon={Bathtub}
                label="Aantal badkamers"
                field="inspectionNumBathrooms" 
                value={(unit as any).inspectionNumBathrooms}
                isLoading={isLoadingUnit}
                isRequired={true}
                onSave={handleSave}
                isSaving={savingField === 'inspectionNumBathrooms'}
                showSuccess={savedField === 'inspectionNumBathrooms'}
                validationError={validationErrors.inspectionNumBathrooms}
              />
              <EditableNumberField
                icon={Drop}
                label="Aantal toiletten"
                field="inspectionNumToilets"
                value={(unit as any).inspectionNumToilets}
                isLoading={isLoadingUnit}
                onSave={handleSave}
                isSaving={savingField === 'inspectionNumToilets'}
                showSuccess={savedField === 'inspectionNumToilets'}
              />
            </div>
          </div>

          {/* Broker (Makelaar) Section */}
          <div className="p-3 border-b border-neutral-primary/10">
            <div className="flex items-center gap-2 mb-2">
              <Handshake size={16} className="text-neutral-primary" />
              <span className="font-medium text-neutral-primary">Makelaars</span>
            </div>
            <div className="mb-3">
              <p className="text-xs text-neutral-secondary-mid italic">
                Koppel één of meer makelaars die de leegstand van deze unit behandelen. Bij een leegstandsmail kan je de kandidaat naar alle gekoppelde makelaars doorverwijzen.
              </p>
            </div>
            <div className="w-full">
              <BrokerListCell
                brokers={unit.brokerContacts}
                pickerTitle={`Selecteer makelaar voor ${unit.id}`}
                onChange={async (next) => {
                  try {
                    const response = await fetchWithAuth(
                      `${API_URL}/units/update/${unit.id}`,
                      {
                        method: 'PUT',
                        body: JSON.stringify({
                          id: unit.id,
                          brokerContacts: next,
                        }),
                        headers: {
                          'Content-Type': 'application/json',
                        },
                      },
                      session
                    );

                    if (response.ok) {
                      if (onUnitUpdate) {
                        onUnitUpdate({ brokerContacts: next } as any);
                      }
                      toast.success(
                        next.length > 0 ? 'Makelaars bijgewerkt' : 'Alle makelaars ontkoppeld',
                      );
                    } else {
                      toast.error('Fout bij opslaan');
                    }
                  } catch (error) {
                    console.error('Error updating broker contacts:', error);
                    toast.error('Fout bij opslaan');
                  }
                }}
              />
            </div>
          </div>

          {/* Visitation Responsible Contact Section */}
          <div className="p-3 border-b border-neutral-primary/10">
            <div className="flex items-center gap-2 mb-2">
              <UserCircle size={16} className="text-neutral-primary" />
              <span className="font-medium text-neutral-primary">Bezichtigingsverantwoordelijke</span>
            </div>
            <div className="mb-3">
              <p className="text-xs text-neutral-secondary-mid italic">
                Deze persoon zal bij een nieuwe afspraak steeds een e-mail krijgen met een geupdatete lijst van bezoekmomenten en contactgegevens van de kandidaten
              </p>
            </div>
            <div className="w-full">
              <VisitationContactCell
                unit={unit}
                onUpdate={async (contactData) => {
                  try {
                    // Save to backend first
                    const response = await fetchWithAuth(
                      `${API_URL}/units/update/${unit.id}`,
                      {
                        method: 'PUT',
                        body: JSON.stringify({ 
                          id: unit.id, 
                          visitationResponsibleContact: contactData 
                        }),
                        headers: {
                          'Content-Type': 'application/json'
                        }
                      },
                      session
                    );

                    if (response.ok) {
                      // Update local state only after successful save
                      if (onUnitUpdate) {
                        onUnitUpdate({ visitationResponsibleContact: contactData });
                      }
                      toast.success('Bezichtigingsverantwoordelijke bijgewerkt');
                    } else {
                      toast.error('Fout bij opslaan');
                    }
                  } catch (error) {
                    console.error('Error updating visitation responsible contact:', error);
                    toast.error('Fout bij opslaan');
                  }
                }}
              />
            </div>
          </div>

          {/* General Remarks Section */}
          <EditableTextField
            icon={SidebarSimple}
            label="Details (bv: Aanbellen bij 'Mertens')"
            field="inspectionGeneralRemarks"
            value={(unit as any).inspectionGeneralRemarks}
            isLoading={isLoadingUnit}
            isTextarea={true}
            onSave={handleSave}
            isSaving={savingField === 'inspectionGeneralRemarks'}
            showSuccess={savedField === 'inspectionGeneralRemarks'}
          />

        <div className="p-3">
          <h4 className="font-medium text-neutral-primary mb-3 flex items-center gap-2">
            <Cube size={16} />
            Voorzieningen
          </h4>
          <div className="space-y-0">
            <StatusField
              icon={WifiHigh}
              label="Wifi aansluiting"
              field="wifiAvailable"
              value={unit.wifiAvailable}
              options={[
                { value: 'Beschikbaar', label: 'Beschikbaar' },
                { value: 'Niet beschikbaar', label: 'Niet beschikbaar' },
                { value: 'Onbekend', label: 'Onbekend' }
              ]}
              type="available"
              isLoading={isLoadingUnit}
            />
            
            <StatusField
              icon={Car}
              label="Parkeermogelijkheden"
              field="parkingAvailableForRental"
              value={unit.parkingAvailableForRental}
              options={[
                { value: 'Beschikbaar', label: 'Beschikbaar' },
                { value: 'Niet beschikbaar', label: 'Niet beschikbaar' },
                { value: 'Onbekend', label: 'Onbekend' }
              ]}
              type="available"
              isLoading={isLoadingUnit}
            />

            <StatusField
              icon={VideoCamera}
              label="Toegang op afstand"
              field="remoteDoorControlAvailable"
              value={unit.remoteDoorControlAvailable}
              options={[
                { value: 'Videofoon', label: 'Videofoon' },
                { value: 'Analoge toegang', label: 'Analoge toegang' },
                { value: 'Geen toegang op afstand mogelijk', label: 'Geen toegang op afstand' },
                { value: 'Onbekend', label: 'Onbekend' }
              ]}
              type="remote"
              isLoading={isLoadingUnit}
            />

            <StatusField
              icon={Buildings}
              label="Verhuur aan bedrijven"
              field="canBeRentedByCompany"
              value={unit.canBeRentedByCompany}
              options={[
                { value: 'Ja', label: 'Ja' },
                { value: 'Nee', label: 'Nee' },
                { value: 'Onbekend', label: 'Onbekend' }
              ]}
              type="yesno"
              isLoading={isLoadingUnit}
            />

            <StatusField
              icon={Drop}
              label="Rookmelder"
              field="smokeDetectorAvailable"
              value={unit.smokeDetectorAvailable}
              options={[
                { value: 'Beschikbaar', label: 'Beschikbaar' },
                { value: 'Niet beschikbaar', label: 'Niet beschikbaar' },
                { value: 'Onbekend', label: 'Onbekend' }
              ]}
              type="available"
              isLoading={isLoadingUnit}
            />

            <StatusField
              icon={WashingMachine}
              label="Wasmachine"
              field="washingMachineAvailable"
              value={unit.washingMachineAvailable}
              options={[
                { value: 'Beschikbaar', label: 'Beschikbaar' },
                { value: 'Niet beschikbaar', label: 'Niet beschikbaar' },
                { value: 'Onbekend', label: 'Onbekend' }
              ]}
              type="available"
              isLoading={isLoadingUnit}
            />

            <StatusField
              icon={Bathtub}
              label="Bad"
              field="bathAvailable"
              value={unit.bathAvailable}
              options={[
                { value: 'Beschikbaar', label: 'Beschikbaar' },
                { value: 'Niet beschikbaar', label: 'Niet beschikbaar' },
                { value: 'Onbekend', label: 'Onbekend' }
              ]}
              type="available"
              isLoading={isLoadingUnit}
            />

            <StatusField
              icon={Shower}
              label="Douche"
              field="showerAvailable"
              value={unit.showerAvailable}
              options={[
                { value: 'Beschikbaar', label: 'Beschikbaar' },
                { value: 'Niet beschikbaar', label: 'Niet beschikbaar' },
                { value: 'Onbekend', label: 'Onbekend' }
              ]}
              type="available"
              isLoading={isLoadingUnit}
            />
          </div>
        </div>

        <div className="p-3">
          <h4 className="font-medium text-neutral-primary mb-3 flex items-center gap-2">
            <Cube size={16} />
            Afmetingen & Ruimte
          </h4>
          <div className="space-y-0">
            <EditableNumberField
              icon={Cube}
              label="Bruto oppervlakte (m²)"
              field="grossSurface"
              value={unit.grossSurface}
              isLoading={isLoadingUnit}
              onSave={handleSave}
              isSaving={savingField === 'grossSurface'}
              showSuccess={savedField === 'grossSurface'}
            />

            <EditableNumberField
              icon={Cube}
              label="Netto oppervlakte (m²)"
              field="netSurface"
              value={unit.netSurface}
              isLoading={isLoadingUnit}
              onSave={handleSave}
              isSaving={savingField === 'netSurface'}
              showSuccess={savedField === 'netSurface'}
            />

            <StatusField
              icon={Buildings}
              label="Gemeubeld"
              field="furnished"
              value={unit.furnished === true ? 'Ja' : unit.furnished === false ? 'Nee' : 'Onbekend'}
              options={[
                { value: 'Ja', label: 'Ja' },
                { value: 'Nee', label: 'Nee' },
                { value: 'Onbekend', label: 'Onbekend' }
              ]}
              type="yesno"
              isLoading={isLoadingUnit}
            />

            <SelectField
              icon={TreePalm}
              label="Buitenruimte"
              field="outsideSpace"
              value={unit.outsideSpace}
              options={[
                { value: 'Geen Buitenruimte', label: 'Geen Buitenruimte' },
                { value: 'Terras', label: 'Terras' },
                { value: 'Tuin', label: 'Tuin' }
              ]}
            />

            {/*
              Shown as soon as the unit is not explicitly marked as "Geen
              Buitenruimte" — including when the type has not been chosen yet —
              so the m² terras/buitenruimte can always be filled in.
            */}
            {unit.outsideSpace !== 'Geen Buitenruimte' && (
              <>
                <NumberField
                  icon={TreePalm}
                  label="Buitenruimte oppervlakte (m²)"
                  field="outsideSpaceSurface"
                  value={unit.outsideSpaceSurface}
                  mask="surface"
                />

                <SelectField
                  icon={Compass}
                  label="Oriëntatie buitenruimte"
                  field="orientationOutsideSpace"
                  value={unit.orientationOutsideSpace}
                  options={[
                    { value: 'N', label: 'Noord' },
                    { value: 'NO', label: 'Noordoost' },
                    { value: 'O', label: 'Oost' },
                    { value: 'ZO', label: 'Zuidoost' },
                    { value: 'Z', label: 'Zuid' },
                    { value: 'ZW', label: 'Zuidwest' },
                    { value: 'W', label: 'West' },
                    { value: 'NW', label: 'Noordwest' }
                  ]}
                />
              </>
            )}
          </div>
        </div>

        {/* EPC Section */}
        <div className="p-3 border-t border-neutral-primary/10">
          <h4 className="font-medium text-neutral-primary mb-3 flex items-center gap-2">
            <Lightning size={16} />
            Energieprestatie (EPC)
          </h4>
          
          {isLoadingEpcs ? (
            <div className="p-3 text-center text-neutral-primary/60">
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-moss-primary border-t-transparent rounded-full animate-spin"></div>
                <p className="text-xs">EPC gegevens laden...</p>
              </div>
            </div>
          ) : latestEpc ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-brick-secondary-fine rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`px-3 py-1 rounded-full text-sm font-bold ${getEpcLabelColor(latestEpc.epcLabel)}`}>
                    {latestEpc.epcLabel}
                  </div>
                  <div className="text-sm">
                    <div className="font-medium text-neutral-primary">
                      {latestEpc.kWh ? `${latestEpc.kWh} kWh/m²/jaar` : 'Geen kWh-gegevens'}
                    </div>
                    <div className="text-xs text-neutral-primary/60 flex items-center gap-1">
                      <Check size={12} className="text-status-good" />
                      {latestEpc.fileLog ? formatEpcDate(latestEpc.fileLog.documentDate) : 'Geen datum'} (meest recent)
                    </div>
                  </div>
                </div>
                {latestEpc.fileLog?.sharePointFileUrl && (
                  <a 
                    href={latestEpc.fileLog.sharePointFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-moss-primary text-moss-primary hover:bg-moss-primary/10 transition-colors"
                    title="EPC document bekijken"
                  >
                    <Eye size={16} />
                  </a>
                )}
              </div>
              
              {latestEpc && (latestEpc.uValueWalls || latestEpc.uValueGlazing || latestEpc.uValueWindows) && (
                <div className="bg-white border border-neutral-primary/10 rounded-lg overflow-hidden">
                  <div className="p-2 bg-neutral-primary/5">
                    <h5 className="text-xs font-medium text-neutral-primary">U-waardes</h5>
                  </div>
                  <div className="p-2 space-y-1">
                    {latestEpc.uValueWalls && (
                      <div className="flex justify-between text-xs">
                        <span>Muren:</span>
                        <span className="font-mono">{latestEpc.uValueWalls.toFixed(2)} W/m²K</span>
                      </div>
                    )}
                    {latestEpc.uValueGlazing && (
                      <div className="flex justify-between text-xs">
                        <span>Beglazing:</span>
                        <span className="font-mono">{latestEpc.uValueGlazing.toFixed(2)} W/m²K</span>
                      </div>
                    )}
                    {latestEpc.uValueWindows && (
                      <div className="flex justify-between text-xs">
                        <span>Ramen:</span>
                        <span className="font-mono">{latestEpc.uValueWindows.toFixed(2)} W/m²K</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {epcs.length > 1 && (
                <div className="space-y-2">
                  <div className="text-xs text-neutral-primary/60 text-center">
                    + {epcs.length - 1} oudere EPC{epcs.length > 2 ? "'s" : ""} beschikbaar
                  </div>
                  <div className="flex flex-wrap gap-1 justify-center">
                    {epcs
                      .sort((a, b) => {
                        if (!a.fileLog?.documentDate && !b.fileLog?.documentDate) return 0;
                        if (!a.fileLog?.documentDate) return 1;
                        if (!b.fileLog?.documentDate) return -1;
                        return new Date(b.fileLog.documentDate).getTime() - new Date(a.fileLog.documentDate).getTime();
                      })
                      .map((epc, index) => (
                        <div key={epc.id} className="flex items-center gap-1">
                          <div className={`w-5 h-5 rounded-sm text-xs flex items-center justify-center font-bold ${getEpcLabelColor(epc.epcLabel)}`}>
                            {epc.epcLabel}
                          </div>
                          {index === 0 && <Check size={10} className="text-status-good" />}
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-3 text-center text-neutral-primary/60">
              <div className="flex flex-col items-center gap-2">
                <Lightning size={20} className="opacity-50" />
                <p className="text-xs">Geen EPC gegevens gevonden</p>
                <p className="text-xs text-neutral-primary/40">Upload EPC documenten met label "EPC unit"</p>
              </div>
            </div>
          )}
        </div>

        {/* Immoweb Reference Section */}
        <ImmowebReferenceField />

        <div className="p-3">
          <h4 className="font-medium text-neutral-primary mb-3 flex items-center gap-2">
            <DeviceMobile size={16} />
            Apparaten
          </h4>
          <DevicesTable />
        </div>
      </div>

      {/* Device Detail/Add Modal */}
      <DeviceDetailModal
        isOpen={isDeviceModalOpen}
        onClose={handleCloseDeviceModal}
        unit={unit}
        device={isAddingDevice ? null : selectedDevice}
        fetchDevices={fetchDevices}
      />
    </div>
  );
};
